import React, { useState, useEffect } from "react";
import api from "../../services/api";
import {
  FiSettings,
  FiSave,
  FiEye,
  FiEyeOff,
  FiCreditCard,
  FiDollarSign,
  FiKey,
  FiGlobe,
  FiPlus,
  FiTrash2,
  FiEdit2,
  FiCheck,
  FiX,
} from "react-icons/fi";

const PROVIDER_OPTIONS = [
  { value: "stripe", label: "Stripe", accounts: ["stripe_de", "stripe_br"] },
  {
    value: "mercadopago",
    label: "MercadoPago",
    accounts: ["mercadopago"],
  },
  { value: "btcpay", label: "BTCPay Server", accounts: ["btcpay"] },
];

const DEFAULT_RULE_FORM = {
  provider: "stripe",
  providerAccount: "stripe_de",
  regionType: "continent",
  regions: [],
  priority: 0,
};

function AdminConfig() {
  const [applications, setApplications] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("providers");
  const [showSecrets, setShowSecrets] = useState({});

  // Routing rules state
  const [routingRules, setRoutingRules] = useState([]);
  const [continents, setContinents] = useState([]);
  const [loadingRules, setLoadingRules] = useState(false);
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm, setRuleForm] = useState({ ...DEFAULT_RULE_FORM });
  const [savingRule, setSavingRule] = useState(false);

  useEffect(() => {
    fetchApplications();
    fetchContinents();
  }, []);

  useEffect(() => {
    if (selectedApp && activeTab === "routing") {
      fetchRoutingRules(selectedApp);
    }
  }, [selectedApp, activeTab]);

  const fetchApplications = async () => {
    try {
      const response = await api.get("/configuration");
      setApplications(response.data.data || []);
      if (response.data.data?.length > 0) {
        selectApplication(response.data.data[0].application_id);
      }
    } catch (err) {
      console.error("Failed to load applications", err);
    } finally {
      setLoading(false);
    }
  };

  const selectApplication = async (appId) => {
    setSelectedApp(appId);
    setLoading(true);
    try {
      const response = await api.get(`/configuration/${appId}`);
      setConfig(response.data.data);
    } catch (err) {
      console.error("Failed to load config", err);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put(`/configuration/${selectedApp}`, config);
      alert("Configuration saved successfully");
    } catch (err) {
      alert("Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const updateProviderConfig = (provider, field, value) => {
    setConfig((prev) => ({
      ...prev,
      providers: {
        ...prev.providers,
        [provider]: {
          ...prev.providers?.[provider],
          [field]: value,
        },
      },
    }));
  };

  const toggleSecret = (key) => {
    setShowSecrets((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Routing rules functions
  const fetchContinents = async () => {
    try {
      const response = await api.get("/provider-routing/continents");
      setContinents(response.data.data || []);
    } catch (err) {
      console.error("Failed to load continents", err);
    }
  };

  const fetchRoutingRules = async (appId) => {
    setLoadingRules(true);
    try {
      const response = await api.get(`/provider-routing/${appId}`);
      setRoutingRules(response.data.data || []);
    } catch (err) {
      console.error("Failed to load routing rules", err);
      setRoutingRules([]);
    } finally {
      setLoadingRules(false);
    }
  };

  const openNewRuleForm = () => {
    setEditingRule(null);
    setRuleForm({ ...DEFAULT_RULE_FORM });
    setShowRuleForm(true);
  };

  const openEditRuleForm = (rule) => {
    setEditingRule(rule);
    setRuleForm({
      provider: rule.provider,
      providerAccount: rule.provider_account,
      regionType: rule.region_type,
      regions: rule.regions || [],
      priority: rule.priority,
    });
    setShowRuleForm(true);
  };

  const cancelRuleForm = () => {
    setShowRuleForm(false);
    setEditingRule(null);
    setRuleForm({ ...DEFAULT_RULE_FORM });
  };

  const toggleRegion = (regionKey) => {
    setRuleForm((prev) => ({
      ...prev,
      regions: prev.regions.includes(regionKey)
        ? prev.regions.filter((r) => r !== regionKey)
        : [...prev.regions, regionKey],
    }));
  };

  const saveRule = async () => {
    setSavingRule(true);
    try {
      if (editingRule) {
        await api.put(`/provider-routing/${editingRule.id}`, {
          provider: ruleForm.provider,
          providerAccount: ruleForm.providerAccount,
          regionType: ruleForm.regionType,
          regions: ruleForm.regionType === "worldwide" ? [] : ruleForm.regions,
          priority: Number(ruleForm.priority),
        });
      } else {
        await api.post("/provider-routing", {
          applicationId: selectedApp,
          provider: ruleForm.provider,
          providerAccount: ruleForm.providerAccount,
          regionType: ruleForm.regionType,
          regions: ruleForm.regionType === "worldwide" ? [] : ruleForm.regions,
          priority: Number(ruleForm.priority),
        });
      }
      cancelRuleForm();
      fetchRoutingRules(selectedApp);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to save routing rule");
    } finally {
      setSavingRule(false);
    }
  };

  const deleteRule = async (ruleId) => {
    if (!window.confirm("Delete this routing rule?")) return;
    try {
      await api.delete(`/provider-routing/${ruleId}`);
      fetchRoutingRules(selectedApp);
    } catch (err) {
      alert("Failed to delete routing rule");
    }
  };

  const toggleRuleActive = async (rule) => {
    try {
      await api.put(`/provider-routing/${rule.id}`, {
        isActive: !rule.is_active,
      });
      fetchRoutingRules(selectedApp);
    } catch (err) {
      alert("Failed to update routing rule");
    }
  };

  // Get accounts for the selected provider in the form
  const currentProviderAccounts =
    PROVIDER_OPTIONS.find((p) => p.value === ruleForm.provider)?.accounts || [];

  if (loading && !config) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  const tabs = [
    { id: "providers", label: "Payment Providers", icon: FiCreditCard },
    { id: "routing", label: "Routing Rules", icon: FiGlobe },
    { id: "webhooks", label: "Webhooks", icon: FiKey },
  ];

  return (
    <div>
      <div className="page-header flex-between">
        <div>
          <h1 className="page-title">Integrations</h1>
          <p className="page-subtitle">
            Configure payment providers, routing, and webhooks
          </p>
        </div>
        <div
          style={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <select
            className="form-input"
            value={selectedApp || ""}
            onChange={(e) => selectApplication(e.target.value)}
            style={{ minWidth: "200px" }}
          >
            {applications.length === 0 && (
              <option value="">No applications configured</option>
            )}
            {applications.map((app) => (
              <option key={app.application_id} value={app.application_id}>
                {app.application_id}
              </option>
            ))}
          </select>
          {activeTab !== "routing" && (
            <button
              className="btn btn-primary"
              onClick={saveConfig}
              disabled={saving || !config}
            >
              <FiSave /> {saving ? "Saving..." : "Save Changes"}
            </button>
          )}
        </div>
      </div>

      {/* Empty state when no applications */}
      {!loading && applications.length === 0 && (
        <div className="card" style={{ textAlign: "center", padding: "48px" }}>
          <FiCreditCard
            style={{
              fontSize: "48px",
              color: "var(--text-secondary)",
              marginBottom: "16px",
            }}
          />
          <h3 style={{ marginBottom: "8px" }}>No Payment Configurations</h3>
          <p style={{ color: "var(--text-secondary)", marginBottom: "24px" }}>
            Payment configurations must be created through the auth-service
            admin panel. Once applications are configured there, you can enable
            payment providers here.
          </p>
        </div>
      )}

      {config && (
        <>
          {/* Tabs */}
          <div className="tabs" style={{ marginBottom: "24px" }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon /> {tab.label}
              </button>
            ))}
          </div>

          {/* Providers Tab */}
          {activeTab === "providers" && (
            <>
              <div
                className="alert alert-info"
                style={{ marginBottom: "24px" }}
              >
                <strong>Centralized Credentials:</strong> Payment provider
                credentials (API keys, secrets) are configured globally in
                docker-compose environment variables. Here you only
                enable/disable providers for this application.
              </div>
              <div className="grid grid-3">
                {/* Stripe */}
                <div className="card">
                  <h3
                    style={{
                      marginBottom: "16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <FiCreditCard /> Stripe
                  </h3>

                  <div className="form-group">
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={config.providers?.stripe?.enabled || false}
                        onChange={(e) =>
                          updateProviderConfig(
                            "stripe",
                            "enabled",
                            e.target.checked,
                          )
                        }
                      />
                      Enabled
                    </label>
                  </div>
                  <p
                    style={{
                      color: "var(--text-secondary)",
                      fontSize: "14px",
                      marginTop: "8px",
                    }}
                  >
                    Credentials configured in docker-compose.yml
                  </p>
                </div>

                {/* Mercado Pago */}
                <div className="card">
                  <h3
                    style={{
                      marginBottom: "16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <FiDollarSign /> Mercado Pago
                  </h3>

                  <div className="form-group">
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={
                          config.providers?.mercadopago?.enabled || false
                        }
                        onChange={(e) =>
                          updateProviderConfig(
                            "mercadopago",
                            "enabled",
                            e.target.checked,
                          )
                        }
                      />
                      Enabled
                    </label>
                  </div>
                  <p
                    style={{
                      color: "var(--text-secondary)",
                      fontSize: "14px",
                      marginTop: "8px",
                    }}
                  >
                    Credentials configured in docker-compose.yml
                  </p>
                </div>

                {/* BTCPay */}
                <div className="card">
                  <h3
                    style={{
                      marginBottom: "16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <FiKey /> BTCPay Server
                  </h3>

                  <div className="form-group">
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={config.providers?.btcpay?.enabled || false}
                        onChange={(e) =>
                          updateProviderConfig(
                            "btcpay",
                            "enabled",
                            e.target.checked,
                          )
                        }
                      />
                      Enabled
                    </label>
                  </div>
                  <p
                    style={{
                      color: "var(--text-secondary)",
                      fontSize: "14px",
                      marginTop: "8px",
                    }}
                  >
                    Credentials configured in docker-compose.yml
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Routing Rules Tab */}
          {activeTab === "routing" && (
            <>
              <div
                className="alert alert-info"
                style={{ marginBottom: "24px" }}
              >
                <strong>Payment Routing:</strong> Rules determine which payment
                provider handles transactions based on the customer's region.
                Higher priority rules take precedence. If no rule matches, the
                system falls back to default routing.
              </div>

              <div className="card">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "16px",
                  }}
                >
                  <h3 style={{ margin: 0 }}>
                    <FiGlobe
                      style={{ marginRight: "8px", verticalAlign: "middle" }}
                    />
                    Routing Rules
                  </h3>
                  {!showRuleForm && (
                    <button
                      className="btn btn-primary"
                      onClick={openNewRuleForm}
                    >
                      <FiPlus /> Add Rule
                    </button>
                  )}
                </div>

                {/* Rule Form */}
                {showRuleForm && (
                  <div
                    style={{
                      background: "var(--bg-tertiary)",
                      borderRadius: "8px",
                      padding: "16px",
                      marginBottom: "16px",
                      border: "1px solid var(--border-color)",
                    }}
                  >
                    <h4 style={{ marginBottom: "12px" }}>
                      {editingRule ? "Edit Rule" : "New Routing Rule"}
                    </h4>
                    <div className="grid grid-2" style={{ gap: "12px" }}>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label">Provider</label>
                        <select
                          className="form-input"
                          value={ruleForm.provider}
                          onChange={(e) => {
                            const provider = e.target.value;
                            const accounts =
                              PROVIDER_OPTIONS.find(
                                (p) => p.value === provider,
                              )?.accounts || [];
                            setRuleForm((prev) => ({
                              ...prev,
                              provider,
                              providerAccount: accounts[0] || "",
                            }));
                          }}
                        >
                          {PROVIDER_OPTIONS.map((p) => (
                            <option key={p.value} value={p.value}>
                              {p.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label">Provider Account</label>
                        <select
                          className="form-input"
                          value={ruleForm.providerAccount}
                          onChange={(e) =>
                            setRuleForm((prev) => ({
                              ...prev,
                              providerAccount: e.target.value,
                            }))
                          }
                        >
                          {currentProviderAccounts.map((acc) => (
                            <option key={acc} value={acc}>
                              {acc}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label">Region Type</label>
                        <select
                          className="form-input"
                          value={ruleForm.regionType}
                          onChange={(e) =>
                            setRuleForm((prev) => ({
                              ...prev,
                              regionType: e.target.value,
                              regions:
                                e.target.value === "worldwide"
                                  ? []
                                  : prev.regions,
                            }))
                          }
                        >
                          <option value="continent">Continent</option>
                          <option value="worldwide">Worldwide</option>
                        </select>
                      </div>

                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label className="form-label">Priority</label>
                        <input
                          type="number"
                          className="form-input"
                          min="0"
                          value={ruleForm.priority}
                          onChange={(e) =>
                            setRuleForm((prev) => ({
                              ...prev,
                              priority: parseInt(e.target.value) || 0,
                            }))
                          }
                        />
                        <small
                          style={{
                            color: "var(--text-secondary)",
                            fontSize: "12px",
                          }}
                        >
                          Higher = preferred when multiple rules match
                        </small>
                      </div>
                    </div>

                    {/* Continent Checkboxes */}
                    {ruleForm.regionType === "continent" && (
                      <div style={{ marginTop: "12px" }}>
                        <label className="form-label">Continents</label>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "8px",
                          }}
                        >
                          {continents.map((c) => (
                            <label
                              key={c.key}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "6px 12px",
                                borderRadius: "6px",
                                border: `1px solid ${ruleForm.regions.includes(c.key) ? "var(--primary)" : "var(--border-color)"}`,
                                background: ruleForm.regions.includes(c.key)
                                  ? "rgba(99, 102, 241, 0.1)"
                                  : "transparent",
                                cursor: "pointer",
                                fontSize: "14px",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={ruleForm.regions.includes(c.key)}
                                onChange={() => toggleRegion(c.key)}
                              />
                              {c.label}
                              <span
                                style={{
                                  color: "var(--text-secondary)",
                                  fontSize: "12px",
                                }}
                              >
                                ({c.countries.length})
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        marginTop: "16px",
                        justifyContent: "flex-end",
                      }}
                    >
                      <button
                        className="btn btn-secondary"
                        onClick={cancelRuleForm}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={saveRule}
                        disabled={
                          savingRule ||
                          (ruleForm.regionType === "continent" &&
                            ruleForm.regions.length === 0)
                        }
                      >
                        {savingRule ? (
                          "Saving..."
                        ) : (
                          <>
                            <FiCheck />{" "}
                            {editingRule ? "Update Rule" : "Create Rule"}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* Rules Table */}
                {loadingRules ? (
                  <div className="loading-container">
                    <div className="spinner"></div>
                  </div>
                ) : routingRules.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "32px",
                      color: "var(--text-secondary)",
                    }}
                  >
                    <FiGlobe
                      style={{ fontSize: "32px", marginBottom: "8px" }}
                    />
                    <p>No routing rules configured</p>
                    <p style={{ fontSize: "13px" }}>
                      Default routing will be used (hardcoded fallback)
                    </p>
                  </div>
                ) : (
                  <div className="table-container">
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "14px",
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          borderBottom: "1px solid var(--border-color)",
                        }}
                      >
                        <th style={{ textAlign: "left", padding: "10px 8px" }}>
                          Provider
                        </th>
                        <th style={{ textAlign: "left", padding: "10px 8px" }}>
                          Account
                        </th>
                        <th style={{ textAlign: "left", padding: "10px 8px" }}>
                          Region
                        </th>
                        <th
                          style={{
                            textAlign: "center",
                            padding: "10px 8px",
                            width: "80px",
                          }}
                        >
                          Priority
                        </th>
                        <th
                          style={{
                            textAlign: "center",
                            padding: "10px 8px",
                            width: "80px",
                          }}
                        >
                          Active
                        </th>
                        <th
                          style={{
                            textAlign: "center",
                            padding: "10px 8px",
                            width: "100px",
                          }}
                        >
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {routingRules.map((rule) => (
                        <tr
                          key={rule.id}
                          style={{
                            borderBottom: "1px solid var(--border-color)",
                            opacity: rule.is_active ? 1 : 0.5,
                          }}
                        >
                          <td style={{ padding: "10px 8px" }}>
                            <span style={{ fontWeight: 500 }}>
                              {
                                PROVIDER_OPTIONS.find(
                                  (p) => p.value === rule.provider,
                                )?.label || rule.provider
                              }
                            </span>
                          </td>
                          <td style={{ padding: "10px 8px" }}>
                            <code
                              style={{
                                fontSize: "13px",
                                background: "var(--bg-secondary)",
                                padding: "2px 6px",
                                borderRadius: "4px",
                              }}
                            >
                              {rule.provider_account}
                            </code>
                          </td>
                          <td style={{ padding: "10px 8px" }}>
                            {rule.region_type === "worldwide" ? (
                              <span className="badge badge-primary">
                                Worldwide
                              </span>
                            ) : (
                              <div
                                style={{
                                  display: "flex",
                                  gap: "4px",
                                  flexWrap: "wrap",
                                }}
                              >
                                {(rule.regions || []).map((r) => (
                                  <span
                                    key={r}
                                    className="badge"
                                    style={{
                                      background: "var(--bg-secondary)",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {r.replace(/_/g, " ")}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td
                            style={{
                              padding: "10px 8px",
                              textAlign: "center",
                              fontWeight: 600,
                            }}
                          >
                            {rule.priority}
                          </td>
                          <td
                            style={{
                              padding: "10px 8px",
                              textAlign: "center",
                            }}
                          >
                            <button
                              onClick={() => toggleRuleActive(rule)}
                              style={{
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                color: rule.is_active
                                  ? "var(--success)"
                                  : "var(--text-secondary)",
                                fontSize: "18px",
                              }}
                              title={
                                rule.is_active ? "Deactivate" : "Activate"
                              }
                            >
                              {rule.is_active ? <FiCheck /> : <FiX />}
                            </button>
                          </td>
                          <td
                            style={{
                              padding: "10px 8px",
                              textAlign: "center",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                gap: "4px",
                                justifyContent: "center",
                              }}
                            >
                              <button
                                onClick={() => openEditRuleForm(rule)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  color: "var(--primary)",
                                  padding: "4px",
                                }}
                                title="Edit"
                              >
                                <FiEdit2 />
                              </button>
                              <button
                                onClick={() => deleteRule(rule.id)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  color: "var(--error)",
                                  padding: "4px",
                                }}
                                title="Delete"
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Webhooks Tab */}
          {activeTab === "webhooks" && (
            <>
              <div className="card" style={{ marginBottom: "24px" }}>
                <h3 style={{ marginBottom: "16px" }}>
                  Inbound Webhooks (From Payment Providers)
                </h3>

                <div
                  className="alert alert-info"
                  style={{ marginBottom: "16px" }}
                >
                  Configure these URLs in your payment provider dashboards to
                  receive webhook events.
                </div>

                <div className="form-group">
                  <label className="form-label">Stripe Webhook URL</label>
                  <input
                    type="text"
                    className="form-input"
                    value={`${window.location.origin}/api/webhooks/stripe`}
                    readOnly
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Mercado Pago Webhook URL</label>
                  <input
                    type="text"
                    className="form-input"
                    value={`${window.location.origin}/api/webhooks/mercadopago`}
                    readOnly
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">BTCPay Webhook URL</label>
                  <input
                    type="text"
                    className="form-input"
                    value={`${window.location.origin}/api/webhooks/btcpay`}
                    readOnly
                  />
                </div>
              </div>

              <div className="card">
                <h3 style={{ marginBottom: "16px" }}>
                  Outbound Webhooks (To Your Application)
                </h3>

                <div
                  className="alert alert-info"
                  style={{ marginBottom: "16px" }}
                >
                  Configure your application's webhook endpoint to receive
                  payment events:
                  <code
                    style={{
                      display: "block",
                      marginTop: "8px",
                      fontSize: "12px",
                    }}
                  >
                    payment.succeeded, subscription.created,
                    subscription.canceled, trial.expired
                  </code>
                </div>

                <div className="form-group">
                  <label className="form-label">Application Webhook URL</label>
                  <input
                    type="url"
                    className="form-input"
                    value={config.webhook?.url || ""}
                    onChange={(e) =>
                      setConfig((prev) => ({
                        ...prev,
                        webhook: {
                          ...prev.webhook,
                          url: e.target.value,
                        },
                      }))
                    }
                    placeholder="https://your-app.com/api/webhooks/payments"
                  />
                  <small
                    style={{
                      color: "var(--text-secondary)",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    Your application will receive POST requests with payment
                    events
                  </small>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Webhook Secret (for signature verification)
                  </label>
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <input
                      type={showSecrets.webhookSecret ? "text" : "password"}
                      className="form-input"
                      value={config.webhook?.secret || ""}
                      onChange={(e) =>
                        setConfig((prev) => ({
                          ...prev,
                          webhook: {
                            ...prev.webhook,
                            secret: e.target.value,
                          },
                        }))
                      }
                      placeholder="whsec_..."
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      className="btn btn-icon"
                      onClick={() => toggleSecret("webhookSecret")}
                    >
                      {showSecrets.webhookSecret ? <FiEyeOff /> : <FiEye />}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => {
                        const secret =
                          "whsec_" +
                          Array.from(
                            { length: 32 },
                            () =>
                              "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"[
                                Math.floor(Math.random() * 62)
                              ],
                          ).join("");
                        setConfig((prev) => ({
                          ...prev,
                          webhook: {
                            ...prev.webhook,
                            secret: secret,
                          },
                        }));
                        setShowSecrets((prev) => ({
                          ...prev,
                          webhookSecret: true,
                        }));
                      }}
                      title="Generate random secret"
                    >
                      <FiKey /> Generate
                    </button>
                  </div>
                  <small
                    style={{
                      color: "var(--text-secondary)",
                      marginTop: "4px",
                      display: "block",
                    }}
                  >
                    Use this secret to verify webhook signatures in your
                    application
                  </small>
                </div>

                <h4 style={{ marginTop: "24px", marginBottom: "16px" }}>
                  Event Notifications
                </h4>

                <div className="form-group">
                  <label className="form-label">Notification Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={config.notifications?.email || ""}
                    onChange={(e) =>
                      setConfig((prev) => ({
                        ...prev,
                        notifications: {
                          ...prev.notifications,
                          email: e.target.value,
                        },
                      }))
                    }
                    placeholder="admin@example.com"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Slack Webhook (optional)</label>
                  <input
                    type="url"
                    className="form-input"
                    value={config.notifications?.slack || ""}
                    onChange={(e) =>
                      setConfig((prev) => ({
                        ...prev,
                        notifications: {
                          ...prev.notifications,
                          slack: e.target.value,
                        },
                      }))
                    }
                    placeholder="https://hooks.slack.com/..."
                  />
                </div>
              </div>
            </>
          )}
        </>
      )}

      <style jsx="true">{`
        .tabs {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          border-bottom: 1px solid var(--border-color);
          padding-bottom: 0;
        }
        .tab {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: none;
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          border-bottom: 2px solid transparent;
          margin-bottom: -1px;
        }
        .tab:hover {
          color: var(--text-primary);
        }
        .tab.active {
          color: var(--primary-color);
          border-bottom-color: var(--primary-color);
        }
        .grid-3 {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
        }
        .alert-info {
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 8px;
          padding: 12px 16px;
          color: #60a5fa;
        }
        @media (max-width: 1024px) {
          .grid-3 {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}

export default AdminConfig;
